import { useAccountCampaignInfo } from '../../client-queries';
import { isB2b } from '../../utils';
import { useProfile } from '../use-profile/use-profile';

type CampaignEligibility = {
  /** true once the /discountinfos type=4 query has resolved successfully */
  isEligibilityLoaded: boolean;
  /** false only when a LOGGED-IN customer's loaded response is empty */
  hasEligibleCampaigns: boolean;
  /** set membership check against the loaded response; safe to call with any string */
  isCampaignEligible: (campaignId: string) => boolean;
  /**
   * [45175] true only when `categories` is non-empty AND every entry is present
   * in that specific campaign's Categories list from the loaded response
   * (all-or-nothing, per-campaign scoped). An empty array and an unknown or
   * not-yet-loaded campaign are never eligible.
   */
  isCategoryTileEligible: (campaignId: string, categories: string[]) => boolean;
};

/** Inert result for the colora (B2C) build: never hides the button, never filters tiles. */
const INERT_ELIGIBILITY: CampaignEligibility = {
  isEligibilityLoaded: false,
  hasEligibleCampaigns: true,
  isCampaignEligible: () => false,
  isCategoryTileEligible: () => true,
};

/**
 * Campaign eligibility derived from the /discountinfos type=4 response
 * (`useAccountCampaignInfo`, ticket 45173) for tickets 45174 and 45175.
 *
 * Drives the "% Acties" header button and campaign-tile visibility
 * (45174) plus category-tile visibility (45175):
 * the button is hidden iff the customer is logged in AND the response
 * has loaded AND it contains zero eligible campaigns. Anonymous
 * customers are S-VAKMAN-scoped by the query itself and are never hidden.
 * Campaign and category tiles default-hide until the response has loaded.
 *
 * [45174] B2B only: the colora (B2C) build must not mount the /discountinfos
 * chain at all (no campaign fetch, no account fetch, no filtering). isB2b is
 * a build-time channel constant, so hook order is stable within every build
 * (module-level build-time hook selection precedent: @boss/auth useProfile).
 */
export const useCampaignEligibility = (locale: string): CampaignEligibility => {
  if (!isB2b) {
    return INERT_ELIGIBILITY;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks -- guarded by the build-time isB2b channel constant above
  const { isLoggedIn } = useProfile();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- guarded by the build-time isB2b channel constant above
  const query = useAccountCampaignInfo(locale);

  // Only successful data counts; loading/error leaves this empty.
  const campaignMap = new Map<string, Set<string>>(
    (query.isSuccess ? query.data : undefined)?.map((info): [string, Set<string>] => [
      info.campaignId,
      new Set(info.categories.map(category => category.category)),
    ]) ?? [],
  );

  return {
    /** true once the /discountinfos type=4 query has resolved successfully */
    isEligibilityLoaded: query.isSuccess,
    /** false only when a LOGGED-IN customer's loaded response is empty */
    hasEligibleCampaigns: !isLoggedIn || (query.isSuccess && campaignMap.size > 0),
    /** set membership check against the loaded response; safe to call with any string */
    isCampaignEligible: (campaignId: string) => campaignMap.has(campaignId),
    isCategoryTileEligible: (campaignId: string, categories: string[]) => {
      const eligible = campaignMap.get(campaignId);

      return !!eligible && categories.length > 0 && categories.every(category => eligible.has(category));
    },
  };
};
